/**
 * ApprovalQueue page — displays Pending VP Approval invoices with coded GL info,
 * amounts, and vendor details. VP can approve or reject with notes.
 *
 * Requirements: 44.15, 44.16, 44.19
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { invoiceService } from '@/services/invoiceService';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';


export function ApprovalQueue() {
  const queryClient = useQueryClient();
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [rejectionNotes, setRejectionNotes] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { data: invoices, isLoading, error, refetch } = useQuery({
    queryKey: ['invoices', 'approval-queue'],
    queryFn: () => invoiceService.getApprovalQueue(),
  });

  const approveMutation = useMutation({
    mutationFn: (invoiceId: number) => invoiceService.approve(invoiceId),
    onSuccess: () => {
      setSuccessMessage('Invoice approved.');
      queryClient.invalidateQueries({ queryKey: ['invoices', 'approval-queue'] });
      setTimeout(() => setSuccessMessage(null), 3000);
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ invoiceId, notes }: { invoiceId: number; notes: string }) =>
      invoiceService.reject(invoiceId, notes),
    onSuccess: () => {
      setSuccessMessage('Invoice rejected and returned to coding queue.');
      setRejectingId(null);
      setRejectionNotes('');
      queryClient.invalidateQueries({ queryKey: ['invoices', 'approval-queue'] });
      setTimeout(() => setSuccessMessage(null), 3000);
    },
  });

  function handleApprove(invoiceId: number) {
    approveMutation.mutate(invoiceId);
  }

  function handleRejectSubmit() {
    if (rejectingId === null || !rejectionNotes.trim()) return;
    rejectMutation.mutate({ invoiceId: rejectingId, notes: rejectionNotes.trim() });
  }

  if (isLoading) {
    return <LoadingSpinner message="Loading approval queue…" />;
  }

  if (error) {
    return (
      <ErrorMessage
        message={(error as Error)?.message ?? 'Failed to load approval queue.'}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>Invoice Approval Queue</h1>
        <StatusBadge label={`${invoices?.length ?? 0} pending`} variant="blue" />
      </div>

      {successMessage && (
        <div style={{ padding: '0.75rem 1rem', backgroundColor: '#dcfce7', border: '1px solid #86efac', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.875rem', color: '#166534' }}>
          {successMessage}
        </div>
      )}

      {!invoices || invoices.length === 0 ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.875rem' }}>
          No invoices pending VP approval in your department.
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
              <th style={{ textAlign: 'left', padding: '0.5rem', color: '#475569' }}>Vendor</th>
              <th style={{ textAlign: 'left', padding: '0.5rem', color: '#475569' }}>Invoice #</th>
              <th style={{ textAlign: 'right', padding: '0.5rem', color: '#475569' }}>Amount</th>
              <th style={{ textAlign: 'left', padding: '0.5rem', color: '#475569' }}>Date</th>
              <th style={{ textAlign: 'left', padding: '0.5rem', color: '#475569' }}>Coded By</th>
              <th style={{ textAlign: 'left', padding: '0.5rem', color: '#475569' }}>Department</th>
              <th style={{ textAlign: 'center', padding: '0.5rem', color: '#475569' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((invoice) => (
              <tr key={invoice.invoiceId} style={{ borderBottom: '1px solid #e2e8f0' }}>
                <td style={{ padding: '0.5rem' }}>
                  <Link
                    to={`/invoices/${invoice.invoiceId}`}
                    style={{ color: '#c8102e', textDecoration: 'none', fontWeight: 500 }}
                  >
                    {invoice.vendorName ?? 'Unknown Vendor'}
                  </Link>
                </td>
                <td style={{ padding: '0.5rem' }}>{invoice.invoiceNumber ?? '—'}</td>
                <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                  {invoice.totalAmount != null ? `$${Number(invoice.totalAmount).toLocaleString()}` : '—'}
                </td>
                <td style={{ padding: '0.5rem' }}>
                  {invoice.invoiceDate ? new Date(invoice.invoiceDate).toLocaleDateString() : '—'}
                </td>
                <td style={{ padding: '0.5rem', color: '#64748b' }}>
                  {invoice.codedBy ?? '—'}
                </td>
                <td style={{ padding: '0.5rem' }}>
                  <StatusBadge label={invoice.departmentScope} variant="blue" />
                </td>
                <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                  <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'center' }}>
                    <button
                      onClick={() => handleApprove(invoice.invoiceId)}
                      disabled={approveMutation.isPending}
                      style={{
                        padding: '0.3rem 0.6rem',
                        borderRadius: '4px',
                        border: 'none',
                        backgroundColor: '#16a34a',
                        color: '#fff',
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                      }}
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => {
                        setRejectingId(invoice.invoiceId);
                        setRejectionNotes('');
                      }}
                      style={{
                        padding: '0.3rem 0.6rem',
                        borderRadius: '4px',
                        border: '1px solid #fca5a5',
                        backgroundColor: '#fff',
                        color: '#dc2626',
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                      }}
                    >
                      Reject
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {approveMutation.error && (
        <p style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: '0.5rem' }}>
          {(approveMutation.error as Error).message}
        </p>
      )}

      {/* Rejection Notes Dialog */}
      {rejectingId !== null && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setRejectingId(null)}
        >
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: '8px',
              padding: '1.5rem',
              maxWidth: '450px',
              width: '100%',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 0.75rem', fontSize: '1.1rem', fontWeight: 600 }}>
              Reject Invoice
            </h3>
            <p style={{ fontSize: '0.875rem', color: '#64748b', margin: '0 0 1rem' }}>
              Please provide rejection notes. The invoice will be returned to the coding queue.
            </p>
            <textarea
              value={rejectionNotes}
              onChange={(e) => setRejectionNotes(e.target.value)}
              placeholder="Enter rejection reason…"
              rows={4}
              aria-label="Rejection notes"
              style={{
                width: '100%',
                padding: '0.5rem',
                borderRadius: '4px',
                border: '1px solid #d1d5db',
                fontSize: '0.875rem',
                resize: 'vertical',
                boxSizing: 'border-box',
              }}
            />

            {rejectMutation.error && (
              <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.5rem 0 0' }}>
                {(rejectMutation.error as Error).message}
              </p>
            )}

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button
                onClick={() => setRejectingId(null)}
                style={{
                  padding: '0.4rem 0.75rem',
                  borderRadius: '4px',
                  border: '1px solid #d1d5db',
                  backgroundColor: '#fff',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleRejectSubmit}
                disabled={!rejectionNotes.trim() || rejectMutation.isPending}
                style={{
                  padding: '0.4rem 0.75rem',
                  borderRadius: '4px',
                  border: 'none',
                  backgroundColor: '#dc2626',
                  color: '#fff',
                  cursor: !rejectionNotes.trim() ? 'not-allowed' : 'pointer',
                  fontSize: '0.8rem',
                  fontWeight: 500,
                }}
              >
                {rejectMutation.isPending ? 'Rejecting…' : 'Reject Invoice'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
